import React from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Checkbox } from '@mui/material';
import { Warning as WarningIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Add as AddIcon, AddCircleOutline as AddCircleOutlineIcon } from '@mui/icons-material';
import AddAccountCardModal from '../Widgets/AddAccountCardModal/AddAccountCardModal';
import AutocompleteSelectFilter from '../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import { toast } from 'react-toastify';
import EnumsService from '../../Services/enums.service';
import styles from './MissingAccountCardsAlert.module.scss';

const MissingAccountCardsAlert = ({ 
    missingCards, 
    direction,
    client,
    token,
    palCodes,
    accountCardOptions,
    fetchEnums,
    setLoading,
    isRange = false,
    isClosedReportingMonth = false,
    sortCodes = []
}) => {
    const { t, i18n } = useTranslation();
    const [expanded, setExpanded] = React.useState(false);
    const [modalOpen, setModalOpen] = React.useState(false);
    const [selectedCard, setSelectedCard] = React.useState(null);
    const [selectedCards, setSelectedCards] = React.useState([]);
    const [selectedPalCode, setSelectedPalCode] = React.useState('');
    const [bulkCreating, setBulkCreating] = React.useState(false);

    const isRtl = direction === 'rtl' || i18n.language === 'he' || i18n.language === 'ar';

    // Filter out duplicate card IDs - keep only unique MissingCard values
    const uniqueCards = React.useMemo(() => {
        if (!missingCards || missingCards.length === 0) {
            return [];
        }
        const seen = new Set();
        return missingCards.filter(card => {
            const cardId = card.missingCard || card.missingCard;
            if (!cardId || seen.has(cardId)) {
                return false;
            }
            seen.add(cardId);
            return true;
        });
    }, [missingCards]);

    if (!missingCards || missingCards.length === 0 || uniqueCards.length === 0) {
        return null;
    }

    const handleCreateCard = (card) => {
        const cardId = card.missingCard || card.MissingCard;
        setSelectedCard({
            accountCodeId: cardId,
            accountCodeName: card.description || card.Description || '',
            accountCaseId: '',
            palCode: ''
        });
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedCard(null);
    };

    const handleChangeAfterAdd = (cardId, type) => {
        // This will be called after a card is successfully created
        // The parent component should refresh the data
        if (fetchEnums) {
            fetchEnums();
        }
    };

    const handleSelectCard = (cardId) => {
        setSelectedCards(prev => {
            if (prev.includes(cardId)) {
                return prev.filter(id => id !== cardId);
            } else {
                return [...prev, cardId];
            }
        });
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedCards(uniqueCards.map(card => card.missingCard || card.MissingCard));
        } else {
            setSelectedCards([]);
        }
    };

    const handleBulkCreate = async () => {
        if (selectedCards.length === 0) {
            toast.warning(t('PleaseSelectCardsToCreate') || 'נא לבחור כרטיסים ליצירה');
            return;
        }

        setBulkCreating(true);
        setLoading(true);

        let successCount = 0;
        let failureCount = 0;
        const errors = [];

        for (const cardId of selectedCards) {
            const card = uniqueCards.find(c => (c.missingCard || c.MissingCard) === cardId);
            if (!card) continue;

            try {
                const response = await EnumsService.AddAccountCard(
                    client.caseId,
                    cardId,
                    card.description || card.Description || '',
                    '', // accountCaseId
                    selectedPalCode || '', // PAL code is optional
                    token
                );

                if (response.ok) {
                    successCount++;
                } else {
                    failureCount++;
                    const errorText = await response.text();
                    errors.push(`${cardId}: ${errorText}`);
                }
            } catch (error) {
                failureCount++;
                errors.push(`${cardId}: ${error.message}`);
            }
        }

        setLoading(false);
        setBulkCreating(false);

        // Show results
        if (successCount > 0) {
            toast.success(
                t('BulkCreateSuccess', { count: successCount }) || 
                `נוצרו בהצלחה ${successCount} כרטיסים`
            );
        }

        if (failureCount > 0) {
            toast.error(
                t('BulkCreatePartialFailure', { success: successCount, failed: failureCount }) || 
                `נוצרו ${successCount} כרטיסים, ${failureCount} נכשלו`
            );
            console.error('Bulk create errors:', errors);
        }

        // Refresh data
        if (successCount > 0 && fetchEnums) {
            await fetchEnums();
        }

        // Clear selections
        setSelectedCards([]);
        setSelectedPalCode('');
    };

    const allSelected = uniqueCards.length > 0 && selectedCards.length === uniqueCards.length;
    const someSelected = selectedCards.length > 0 && selectedCards.length < uniqueCards.length;
    const selectedPalOption = (palCodes || []).find((pc) => pc.value === selectedPalCode) || null;
    const palPlaceholder = `${t('SelectPalCode')} (${t('Optional')})`;

    return (
        <div className={`${styles.root} ${isRtl ? styles.rtl : styles.ltr}`} dir={direction}>
            <button
                type="button"
                className={styles.banner}
                onClick={() => setExpanded(!expanded)}
                aria-expanded={expanded}
                aria-label={t('Expand') || 'הרחב'}
            >
                <span className={styles.bannerIcon} aria-hidden="true">
                    <WarningIcon fontSize="small" />
                </span>
                <span className={styles.bannerCopy}>
                    <span className={styles.bannerTitle}>
                        {t('MissingAccountCards') || 'כרטיסי חשבון חסרים'}
                    </span>
                    <span className={styles.bannerMeta}>
                        {uniqueCards.length} {t('CardsNotFoundInAccountCards') || 'כרטיסים לא נמצאו ב-AccountCards'}
                        {selectedCards.length > 0 && (
                            <span className={styles.selectedCount}>
                                · {selectedCards.length} {t('CardsSelected') || 'כרטיסים נבחרו'}
                            </span>
                        )}
                    </span>
                </span>
                <span className={styles.chevronBtn} aria-hidden="true">
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </span>
            </button>
            
            <Collapse in={expanded}>
                <div className={styles.bulkBar} dir={direction}>
                    <div className={styles.palField}>
                        <AutocompleteSelectFilter
                            options={palCodes || []}
                            selectedValues={selectedPalOption}
                            onChange={(event, newValue) => {
                                setSelectedPalCode(newValue?.value || '');
                            }}
                            label={palPlaceholder}
                            placeholder={palPlaceholder}
                            isMultiple={false}
                            isDisabled={selectedCards.length === 0 || bulkCreating}
                            direction={direction}
                        />
                    </div>

                    <button
                        type="button"
                        className={styles.createBtn}
                        onClick={handleBulkCreate}
                        disabled={selectedCards.length === 0 || bulkCreating}
                    >
                        <AddCircleOutlineIcon fontSize="small" />
                        {bulkCreating 
                            ? (t('Creating') || 'יוצר...') 
                            : (t('BulkCreateCards', { count: selectedCards.length }) || `צור ${selectedCards.length} כרטיסים`)}
                    </button>

                    <p className={styles.help}>
                        {t('BulkCreateHelpOptional') || 'בחר כרטיסים וסיווג מאזני (אופציונלי) כדי ליצור מספר כרטיסים בבת אחת'}
                    </p>
                </div>

                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>
                                    <Checkbox
                                        className={styles.checkbox}
                                        checked={allSelected}
                                        indeterminate={someSelected}
                                        onChange={handleSelectAll}
                                        size="small"
                                    />
                                </th>
                                <th>{t('UploadNumber') || 'מספר העלאה'}</th>
                                <th>{t('Description') || 'תיאור'}</th>
                                <th>{t('CreditAccount') || 'חשבון זכות'}</th>
                                <th>{t('DebitAccount') || 'חשבון חובה'}</th>
                                <th>{t('MissingCard') || 'כרטיס חסר'}</th>
                                <th>{t('Reference') || 'אסמכתא'}</th>
                                <th>{t('Reference2') || 'אסמכתא 2'}</th>
                                <th>{t('Actions') || 'פעולות'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {uniqueCards.map((card, index) => {
                                const cardId = card.missingCard || card.MissingCard;
                                const isSelected = selectedCards.includes(cardId);
                                
                                return (
                                    <tr 
                                        key={index} 
                                        className={isSelected ? styles.rowSelected : undefined}
                                    >
                                        <td>
                                            <Checkbox
                                                className={styles.checkbox}
                                                checked={isSelected}
                                                onChange={() => handleSelectCard(cardId)}
                                                size="small"
                                            />
                                        </td>
                                        <td>{card.uploadNumber || card.UploadNumber || '-'}</td>
                                        <td className={styles.descriptionCell}>
                                            {card.description || card.Description || '-'}
                                        </td>
                                        <td>{card.creditAccount || card.CreditAccount || '-'}</td>
                                        <td>{card.debitAccount || card.DebitAccount || '-'}</td>
                                        <td className={styles.missingCell}>
                                            {card.missingCard || card.MissingCard || '-'}
                                        </td>
                                        <td>{card.reference || card.Reference || '-'}</td>
                                        <td>{card.reference2 || card.Reference2 || '-'}</td>
                                        <td>
                                            {client && token && (
                                                <button
                                                    type="button"
                                                    className={styles.rowBtn}
                                                    onClick={() => handleCreateCard(card)}
                                                >
                                                    <AddIcon fontSize="small" />
                                                    {t('CreateCard') || 'צור כרטיס'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {uniqueCards.length > 0 && (
                    <p className={styles.note}>
                        {t('MissingAccountCardsNote') || 'הכרטיסים הבאים מופיעים בקובץ MDB אך לא נמצאים במערכת. יש לייבא כרטיסי חשבון קיימים או ליצורם באופן מתאים.'}
                    </p>
                )}
            </Collapse>

            {client && token && (
                <AddAccountCardModal
                    open={modalOpen}
                    onClose={handleModalClose}
                    editedItem={selectedCard}
                    isRange={isRange}
                    isClosedReportingMonth={isClosedReportingMonth}
                    palCodes={palCodes}
                    propPalCode={null}
                    direction={direction}
                    client={client}
                    token={token}
                    setLoading={setLoading}
                    accountCardOptions={accountCardOptions}
                    handleChangeAfterAdd={handleChangeAfterAdd}
                    fetchEnums={fetchEnums}
                    type="main"
                    isEdit={false}
                    sortCodes={sortCodes}
                />
            )}
        </div>
    );
};

export default MissingAccountCardsAlert;
